import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { buildHermesYamlSnippet } from "../settings/build-hermes-yaml-snippet.js";
import { HERMES_BASE_URL } from "../hermes-singleton.js";
import {
  defaultAiModels,
  loadAppSettings,
  saveAppSettings,
} from "../settings/settings-storage.js";
import type { AppSettingsV1, SettingsPageId } from "../settings/settings-types.js";
import { icons } from "../icons.js";

const ADMIN_SESSION_STORAGE_KEY = "ead-expui.explore-admin-session";

type MaskedCredentialRow = {
  purpose: string;
  last4: string;
  updated_at: number;
  source?: "db" | "env";
};

@customElement("ead-app-settings-modal")
export class EadAppSettingsModal extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 2400;
      background: rgba(5, 8, 16, 0.72);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px 16px;
      box-sizing: border-box;
    }

    .panel {
      width: min(920px, 100%);
      max-height: min(90vh, 880px);
      display: grid;
      grid-template-columns: 200px 1fr;
      border-radius: 14px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg-surface, #1c1d21);
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
    }

    @media (max-width: 720px) {
      .panel {
        grid-template-columns: 1fr;
        max-height: 95vh;
      }
      .nav {
        flex-direction: row;
        flex-wrap: wrap;
        border-right: none;
        border-bottom: 1px solid var(--border, #2e2f33);
      }
    }

    .nav {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 12px 10px;
      background: color-mix(in srgb, var(--bg-elevated, #222326) 90%, transparent);
      border-right: 1px solid var(--border, #2e2f33);
    }

    .nav__title {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--muted, #8b8d93);
      padding: 6px 8px 10px;
    }

    .nav__item {
      text-align: left;
      border: 1px solid transparent;
      border-radius: 8px;
      padding: 9px 10px;
      font-size: 13px;
      color: var(--text, #e4e5e7);
      background: transparent;
      cursor: pointer;
    }

    .nav__item:hover:not(:disabled) {
      background: var(--bg-hover, #2a2b30);
    }

    .nav__item--active {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 35%, transparent);
      background: var(--accent-subtle, rgba(110, 168, 254, 0.12));
      color: var(--text, #e4e5e7);
    }

    .nav__item:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .body {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .body__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 18px;
      border-bottom: 1px solid var(--border, #2e2f33);
    }

    .body__head h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      border: 1px solid transparent;
      background: transparent;
      color: var(--muted, #8b8d93);
      cursor: pointer;
    }

    .icon-btn:hover {
      color: var(--text, #e4e5e7);
      background: var(--bg-hover, #2a2b30);
    }

    .icon-btn svg {
      width: 18px;
      height: 18px;
    }

    .scroll {
      padding: 16px 18px 20px;
      overflow: auto;
      flex: 1;
      min-height: 0;
    }

    .section {
      margin-bottom: 22px;
    }

    .section h3 {
      margin: 0 0 6px;
      font-size: 14px;
      font-weight: 600;
    }

    .hint {
      margin: 0 0 12px;
      font-size: 12px;
      line-height: 1.45;
      color: var(--muted, #8b8d93);
    }

    label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      margin-bottom: 4px;
      color: var(--muted, #8b8d93);
    }

    input,
    textarea {
      width: 100%;
      box-sizing: border-box;
      border-radius: 8px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg, #141518);
      color: var(--text, #e4e5e7);
      font-size: 13px;
      padding: 8px 10px;
      font-family: var(--mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace);
    }

    textarea {
      min-height: 160px;
      resize: vertical;
      line-height: 1.4;
    }

    .row-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 12px;
    }

    .btn {
      border-radius: 8px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg-elevated, #222326);
      color: var(--text, #e4e5e7);
      font-size: 13px;
      padding: 8px 14px;
      cursor: pointer;
    }

    .btn:hover {
      background: var(--bg-hover, #2a2b30);
    }

    .btn--primary {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 45%, transparent);
      background: color-mix(in srgb, var(--accent, #6ea8fe) 18%, var(--bg-elevated, #222326));
    }

    .status {
      margin-top: 10px;
      font-size: 12px;
      min-height: 16px;
      color: var(--accent, #6ea8fe);
    }

    .status--err {
      color: #f87171;
    }

    .callout {
      margin-top: 14px;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--accent, #6ea8fe) 22%, var(--border, #2e2f33));
      background: color-mix(in srgb, var(--accent, #6ea8fe) 8%, var(--bg, #141518));
      font-size: 12px;
      line-height: 1.5;
      color: var(--muted, #b8bac0);
    }
  `;

  @property({ type: Boolean }) open = false;

  @state() private _page: SettingsPageId = "ai-models";
  @state() private _bootstrapped: boolean | null = null;
  @state() private _sessionToken = "";
  @state() private _adminPassword = "";
  @state() private _openaiKeyDraft = "";
  @state() private _deepseekKeyDraft = "";
  @state() private _maskedRows: MaskedCredentialRow[] = [];
  @state() private _credBusy = false;
  @state() private _credMessage = "";
  @state() private _credMessageErr = false;
  @state() private _draftReasoning = "";
  @state() private _draftVisionModel = "";
  @state() private _draftVisionBase = "";
  @state() private _yamlPreview = "";
  @state() private _status = "";
  @state() private _statusErr = false;

  private _syncFromStorage() {
    const s = loadAppSettings();
    const ai = s.aiModels || defaultAiModels();
    this._draftReasoning = ai.reasoningModel;
    this._draftVisionModel = ai.visionModel;
    this._draftVisionBase = ai.visionOpenAIBaseUrl;
    this._refreshYaml();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("open") && this.open) {
      this._syncFromStorage();
      this._page = "ai-models";
      this._status = "";
      this._statusErr = false;
      this._sessionToken = localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) || "";
      this._bootstrapped = null;
      this._maskedRows = [];
      this._adminPassword = "";
      this._openaiKeyDraft = "";
      this._deepseekKeyDraft = "";
      this._credMessage = "";
      this._credMessageErr = false;
    }
    if (changed.has("_page") && this._page === "credentials" && this.open) {
      void this._initCredentialsPanel();
    }
  }

  private _refreshYaml() {
    this._yamlPreview = buildHermesYamlSnippet({
      reasoningModel: this._draftReasoning,
      visionModel: this._draftVisionModel,
      visionOpenAIBaseUrl: this._draftVisionBase,
    });
  }

  private _close() {
    this.open = false;
    this.dispatchEvent(new CustomEvent("settings-close", { bubbles: true, composed: true }));
  }

  private _backdropClick(ev: MouseEvent) {
    if (ev.target === ev.currentTarget) this._close();
  }

  private _persistLocal() {
    const next: AppSettingsV1 = {
      version: 1,
      aiModels: {
        reasoningModel: this._draftReasoning.trim(),
        visionModel: this._draftVisionModel.trim(),
        visionOpenAIBaseUrl: this._draftVisionBase.trim(),
      },
    };
    saveAppSettings(next);
    this._status = "Saved in this browser (draft preferences).";
    this._statusErr = false;
  }

  private async _copyYaml() {
    try {
      await navigator.clipboard.writeText(this._yamlPreview);
      this._status = "YAML copied to clipboard.";
      this._statusErr = false;
    } catch {
      this._status = "Could not copy (browser blocked clipboard).";
      this._statusErr = true;
    }
  }

  private _downloadYaml() {
    const blob = new Blob([this._yamlPreview], { type: "text/yaml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hermes-fragment-from-explore-ui.yaml";
    a.click();
    URL.revokeObjectURL(url);
    this._status = "Download started.";
    this._statusErr = false;
  }

  private _adminFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    const tok = this._sessionToken || localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) || "";
    if (tok) {
      headers.set("X-Explore-Admin-Session", tok);
    }
    if (init.body != null && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const base = HERMES_BASE_URL || "";
    return fetch(`${base}${path}`, { ...init, credentials: "include", headers });
  }

  private _setSessionToken(token: string) {
    this._sessionToken = token;
    if (token) {
      localStorage.setItem(ADMIN_SESSION_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    }
  }

  private async _fetchAdminBootstrapped() {
    try {
      const st = await this._adminFetch("/v1/admin/status");
      const stJson = (await st.json()) as { bootstrapped?: boolean; error?: string };
      if (!st.ok) {
        throw new Error(stJson.error || `HTTP ${st.status}`);
      }
      this._bootstrapped = !!stJson.bootstrapped;
    } catch (e) {
      this._bootstrapped = null;
      this._credMessage =
        e instanceof Error
          ? e.message
          : "Could not reach /v1/admin/status on the configured Hermes gateway.";
      this._credMessageErr = true;
    }
  }

  private async _loadMaskedIfSession() {
    const tok = this._sessionToken || localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) || "";
    if (!tok) {
      this._maskedRows = [];
      return;
    }
    const r = await this._adminFetch("/v1/admin/credentials");
    if (r.status === 401) {
      this._setSessionToken("");
      this._maskedRows = [];
      this._credMessage = "Enter your server password again to manage keys.";
      this._credMessageErr = true;
      return;
    }
    const j = (await r.json()) as { purposes?: MaskedCredentialRow[]; error?: string };
    if (!r.ok) {
      throw new Error(j.error || `HTTP ${r.status}`);
    }
    this._maskedRows = j.purposes || [];
  }

  private async _initCredentialsPanel() {
    this._credMessage = "";
    this._credMessageErr = false;
    this._credBusy = true;
    try {
      await this._fetchAdminBootstrapped();
      await this._loadMaskedIfSession();
    } catch (e) {
      this._bootstrapped = null;
      this._credMessage =
        e instanceof Error ? e.message : "Could not reach the gateway admin API.";
      this._credMessageErr = true;
    } finally {
      this._credBusy = false;
    }
  }

  /** One step: first install creates password; later visits use the same password to unlock key fields. */
  private async _unlockServerPassword() {
    const pw = this._adminPassword.trim();
    const firstInstall = this._bootstrapped === false;
    if (firstInstall && pw.length < 8) {
      this._credMessage =
        "Use at least 8 characters. You will reuse this password whenever you change API keys from this browser.";
      this._credMessageErr = true;
      return;
    }
    if (!firstInstall && !pw) {
      this._credMessage =
        "Enter the same server password you chose the first time you opened this tab.";
      this._credMessageErr = true;
      return;
    }
    this._credBusy = true;
    this._credMessage = "";
    this._credMessageErr = false;
    try {
      const tryBootstrap = this._bootstrapped === false;
      const endpoint = tryBootstrap ? "/v1/admin/bootstrap" : "/v1/admin/login";
      let r = await this._adminFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ password: pw }),
      });
      let j = (await r.json()) as { session_token?: string; error?: string };
      if (!r.ok && tryBootstrap && j.error === "already_bootstrapped") {
        await this._fetchAdminBootstrapped();
        r = await this._adminFetch("/v1/admin/login", {
          method: "POST",
          body: JSON.stringify({ password: pw }),
        });
        j = (await r.json()) as { session_token?: string; error?: string };
      }
      if (!r.ok) {
        const msg =
          j.error === "invalid_credentials"
            ? "That password is not correct."
            : j.error === "password_too_short"
              ? "Password must be at least 8 characters."
              : j.error || `HTTP ${r.status}`;
        throw new Error(msg);
      }
      if (j.session_token) {
        this._setSessionToken(j.session_token);
      }
      this._adminPassword = "";
      this._credMessage = tryBootstrap
        ? "Password saved. Enter your API keys below when you are ready."
        : "Unlocked. You can enter or update API keys below.";
      this._credMessageErr = false;
      await this._fetchAdminBootstrapped();
      await this._loadMaskedIfSession();
    } catch (e) {
      this._credMessage = e instanceof Error ? e.message : "Could not continue.";
      this._credMessageErr = true;
    } finally {
      this._credBusy = false;
    }
  }

  private async _logoutAdmin() {
    this._credBusy = true;
    try {
      await this._adminFetch("/v1/admin/logout", { method: "POST" });
    } catch {
      /* ignore */
    } finally {
      this._setSessionToken("");
      this._maskedRows = [];
      this._openaiKeyDraft = "";
      this._deepseekKeyDraft = "";
      this._credMessage = "Locked. Keys are hidden until you enter the password again.";
      this._credMessageErr = false;
      this._credBusy = false;
    }
  }

  private async _saveGatewayCredentials() {
    if (!this._sessionToken && !localStorage.getItem(ADMIN_SESSION_STORAGE_KEY)) {
      this._credMessage = "Enter your server password above, then save.";
      this._credMessageErr = true;
      return;
    }
    const keys: Record<string, string> = {};
    const o = this._openaiKeyDraft.trim();
    const d = this._deepseekKeyDraft.trim();
    if (o) keys.openai_api = o;
    if (d) keys.deepseek_api = d;
    if (Object.keys(keys).length === 0) {
      this._credMessage = "Enter at least one key to save, or use Refresh only.";
      this._credMessageErr = true;
      return;
    }
    this._credBusy = true;
    this._credMessage = "";
    try {
      const r = await this._adminFetch("/v1/admin/credentials", {
        method: "PUT",
        body: JSON.stringify({ keys }),
      });
      const j = (await r.json()) as { purposes?: MaskedCredentialRow[]; error?: string };
      if (r.status === 401) {
        this._setSessionToken("");
        this._maskedRows = [];
        throw new Error("Session expired. Enter your server password again.");
      }
      if (!r.ok) {
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      this._maskedRows = j.purposes || [];
      this._openaiKeyDraft = "";
      this._deepseekKeyDraft = "";
      this._credMessage =
        "Saved on the gateway. Keys are applied in-process; restart the gateway if a provider does not pick them up.";
      this._credMessageErr = false;
    } catch (e) {
      this._credMessage = e instanceof Error ? e.message : "Save failed.";
      this._credMessageErr = true;
    } finally {
      this._credBusy = false;
    }
  }

  private async _refreshMaskedCredentials() {
    if (!this._sessionToken && !localStorage.getItem(ADMIN_SESSION_STORAGE_KEY)) {
      this._credMessage = "Enter your server password and Continue first.";
      this._credMessageErr = true;
      return;
    }
    this._credBusy = true;
    this._credMessage = "";
    try {
      const r = await this._adminFetch("/v1/admin/credentials");
      const j = (await r.json()) as { purposes?: MaskedCredentialRow[]; error?: string };
      if (r.status === 401) {
        this._setSessionToken("");
        this._maskedRows = [];
        throw new Error("Enter your server password again.");
      }
      if (!r.ok) {
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      this._maskedRows = j.purposes || [];
      this._credMessage = "List updated.";
      this._credMessageErr = false;
    } catch (e) {
      this._credMessage = e instanceof Error ? e.message : "Refresh failed.";
      this._credMessageErr = true;
    } finally {
      this._credBusy = false;
    }
  }

  private _formatCredTime(ts: number) {
    try {
      return new Date(ts * 1000).toLocaleString();
    } catch {
      return String(ts);
    }
  }

  private _renderCredentials() {
    if (this._credBusy && this._bootstrapped === null) {
      return html`<p class="hint">Loading gateway admin status…</p>`;
    }
    if (this._bootstrapped === null) {
      return html`<p class="hint">Could not load admin status.</p>`;
    }

    const banner =
      this._credMessage !== ""
        ? html`<div class="status ${this._credMessageErr ? "status--err" : ""}">${this._credMessage}</div>`
        : null;

    const hasSession = !!(this._sessionToken || localStorage.getItem(ADMIN_SESSION_STORAGE_KEY));

    const explainer = html`
      <div class="callout">
        <strong>Gateway credentials bridge</strong>: This page saves provider keys through
        <code>/v1/admin/*</code> on the running Hermes gateway. Keys are encrypted in
        <code>~/.hermes/explore_control.db</code> and applied to process env at runtime.
      </div>
    `;

    if (!hasSession) {
      const first = !this._bootstrapped;
      return html`
        ${explainer}
        ${banner}
        <div class="section">
          <h3>${first ? "Set server password" : "Unlock API key manager"}</h3>
          <p class="hint">
            ${first
              ? html`First use on this gateway: choose a password (8+ chars). You will use the same
                  password later to unlock key edits from this browser.`
              : html`Enter the existing server password to unlock API key editing.`}
          </p>
          <label for="ead-admin-password">${first ? "New password" : "Password"}</label>
          <input
            id="ead-admin-password"
            type="password"
            autocomplete="current-password"
            .value=${this._adminPassword}
            @input=${(e: Event) => {
              this._adminPassword = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter" && !this._credBusy) void this._unlockServerPassword();
            }}
          />
          <div class="row-actions">
            <button
              type="button"
              class="btn btn--primary"
              ?disabled=${this._credBusy}
              @click=${() => void this._unlockServerPassword()}
            >
              ${first ? "Set password & continue" : "Change settings (unlock)"}
            </button>
            <button
              type="button"
              class="btn"
              ?disabled=${this._credBusy}
              @click=${() => void this._initCredentialsPanel()}
            >
              Refresh status
            </button>
          </div>
        </div>
      `;
    }

    return html`
      ${explainer}
      ${banner}
      <div class="section">
        <h3>OpenAI &amp; DeepSeek keys</h3>
        <p class="hint">
          Keys are encrypted on the gateway (<code>~/.hermes/explore_control.db</code>) and applied
          to <code>OPENAI_API_KEY</code> / <code>DEEPSEEK_API_KEY</code> for Hermes. Only type a new
          value when rotating; empty fields are not sent and existing stored keys are unchanged.
        </p>
        <label for="ead-cred-openai">OpenAI API key (vision / OpenAI routes)</label>
        <input
          id="ead-cred-openai"
          type="password"
          autocomplete="off"
          .value=${this._openaiKeyDraft}
          @input=${(e: Event) => {
            this._openaiKeyDraft = (e.target as HTMLInputElement).value;
          }}
        />
        <label for="ead-cred-deepseek" style="margin-top:10px">DeepSeek API key</label>
        <input
          id="ead-cred-deepseek"
          type="password"
          autocomplete="off"
          .value=${this._deepseekKeyDraft}
          @input=${(e: Event) => {
            this._deepseekKeyDraft = (e.target as HTMLInputElement).value;
          }}
        />
        <div class="row-actions">
          <button
            type="button"
            class="btn btn--primary"
            ?disabled=${this._credBusy}
            @click=${() => void this._saveGatewayCredentials()}
          >
            Save keys on gateway
          </button>
          <button
            type="button"
            class="btn"
            ?disabled=${this._credBusy}
            @click=${() => void this._refreshMaskedCredentials()}
          >
            Refresh list
          </button>
          <button
            type="button"
            class="btn"
            ?disabled=${this._credBusy}
            @click=${() => void this._logoutAdmin()}
          >
            Lock
          </button>
        </div>
      </div>

      <div class="section">
        <h3>Stored credentials (masked)</h3>
        ${this._maskedRows.length === 0
          ? html`<p class="hint">No keys stored yet.</p>`
          : html`
              <ul class="hint" style="margin:0;padding-left:18px;line-height:1.6">
                ${this._maskedRows.map(
                  (row) => html`
                    <li>
                      <code>${row.purpose}</code> — last four:
                      <strong>${row.last4}</strong>,
                      ${row.source === "env"
                        ? html`from <code>.env</code> fallback`
                        : html`updated ${this._formatCredTime(row.updated_at)}`}
                    </li>
                  `,
                )}
              </ul>
            `}
      </div>
    `;
  }

  private _renderAiModels() {
    return html`
      <div class="section">
        <h3>Reasoning (main agent)</h3>
        <p class="hint">
          Hermes uses the top-level <code>model</code> field for chat and EAD runs. Use the
          <code>provider/model</code> form (for example <code>deepseek/deepseek-chat</code> or
          <code>deepseek/deepseek-reasoner</code>). You can store <code>DEEPSEEK_API_KEY</code> on the
          gateway from Settings → <strong>API keys</strong>, or set it in <code>~/.hermes/.env</code>.
        </p>
        <label for="ead-set-reasoning">Model</label>
        <input
          id="ead-set-reasoning"
          type="text"
          .value=${this._draftReasoning}
          @input=${(e: Event) => {
            this._draftReasoning = (e.target as HTMLInputElement).value;
            this._refreshYaml();
          }}
        />
      </div>

      <div class="section">
        <h3>Vision (screenshots &amp; images)</h3>
        <p class="hint">
          Mapped to Hermes <code>auxiliary.vision</code> with a direct OpenAI-compatible endpoint
          (OpenAI vision by default). Empty <code>api_key</code> in the YAML uses
          <code>OPENAI_API_KEY</code>; set it under Settings → <strong>API keys</strong> on the
          gateway, or in <code>~/.hermes/.env</code>.
        </p>
        <label for="ead-set-vision-base">OpenAI-compatible base URL</label>
        <input
          id="ead-set-vision-base"
          type="text"
          .value=${this._draftVisionBase}
          @input=${(e: Event) => {
            this._draftVisionBase = (e.target as HTMLInputElement).value;
            this._refreshYaml();
          }}
        />
        <label for="ead-set-vision-model" style="margin-top:10px">Vision model id</label>
        <input
          id="ead-set-vision-model"
          type="text"
          .value=${this._draftVisionModel}
          @input=${(e: Event) => {
            this._draftVisionModel = (e.target as HTMLInputElement).value;
            this._refreshYaml();
          }}
        />
      </div>

      <div class="section">
        <h3>Merge snippet for Hermes</h3>
        <p class="hint">
          The web UI cannot safely push model strings to your gateway host automatically. Copy this
          fragment into <code>~/.hermes/config.yaml</code>, merge with your existing file, then
          restart <code>hermes gateway</code> if needed. API keys can be stored encrypted from
          Settings → <strong>API keys</strong> after unlocking with the server password.
        </p>
        <textarea readonly .value=${this._yamlPreview}></textarea>
        <div class="row-actions">
          <button type="button" class="btn btn--primary" @click=${() => this._persistLocal()}>Save draft</button>
          <button type="button" class="btn" @click=${() => this._copyYaml()}>Copy YAML</button>
          <button type="button" class="btn" @click=${() => this._downloadYaml()}>Download .yaml</button>
        </div>
        <div class="callout">
          <strong>Heads up</strong>: this panel stores your last-used model strings in the browser
          only. Hermes still reads live configuration from the machine where the gateway runs; use
          <strong>API keys</strong> for server-side keys.
        </div>
      </div>
    `;
  }

  render() {
    if (!this.open) return html``;

    const title =
      this._page === "ai-models"
        ? "AI models"
        : this._page === "credentials"
          ? "API keys"
          : "Advanced";

    return html`
      <div class="backdrop" @click=${this._backdropClick}>
        <div
          class="panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-settings-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <nav class="nav" aria-label="Settings sections">
            <div class="nav__title">Settings</div>
            <button
              type="button"
              class="nav__item ${this._page === "ai-models" ? "nav__item--active" : ""}"
              @click=${() => {
                this._page = "ai-models";
              }}
            >
              AI models
            </button>
            <button
              type="button"
              class="nav__item ${this._page === "credentials" ? "nav__item--active" : ""}"
              @click=${() => {
                this._page = "credentials";
              }}
            >
              API keys
            </button>
            <button type="button" class="nav__item" disabled title="Coming soon">Advanced</button>
            <button type="button" class="nav__item" disabled title="Coming soon">Notifications</button>
          </nav>
          <div class="body">
            <div class="body__head">
              <h2 id="ead-settings-title">${title}</h2>
              <button type="button" class="icon-btn" @click=${() => this._close()} aria-label="Close settings">
                ${icons.x}
              </button>
            </div>
            <div class="scroll">
              ${this._page === "ai-models"
                ? this._renderAiModels()
                : this._page === "credentials"
                  ? this._renderCredentials()
                  : html`<p class="hint">Nothing here yet.</p>`}
              <div class="status ${this._statusErr ? "status--err" : ""}">${this._status}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
